
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import dftarrowback from "../../assets/task/back_arrow.svg"
import { useLocation, useNavigate } from "react-router-dom";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import InputsNew from "../../components/common/InputsNew";
import DateSelector from "../../components/common/DateSelector";
import InputsDropdown from "../../components/common/InputsDropdown";
import { addHours, addMinutes, differenceInDays, format, isBefore, parse, parseISO } from "date-fns";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import InputsDropdownMultipleSearchSelect from "../../components/common/InputsDropdownMultipleSearchSelect";
import { ApolloError, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { EMPLOYEES_BY_AGENCY_ID, MEETING_TYPES, PROJECT_GOALS, PROJECTS_BY_EMP_ID,PODS_DATA_LIST } from "../../api/query";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import PopupForMessage from "../../components/common/PopupForMessage";
import ExternalAttendees, { Attendee, AgencyOption, EmployeeOption } from "../../components/common/ExternalAttendees";
import { CREATE_MEETING } from "../../api/mutation";
import { getSupabaseExt } from "../../lib/supabase";
import { createZoomMeeting, ZoomLinkType } from "../../lib/zoom";
import BreadCumb from "../../components/common/Breadcrumb";

const AddMeeting: FunctionComponent = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const { userInfo } = useAuth();

    const employeeName = `${userInfo?.first_name} ${userInfo?.last_name}`
    const employeeID = userInfo?.employee_id || '';
    const organizationID = userInfo?.agencies?.id || '';
    const [showMembers, setShowMembers] = useState(false)

    // Get preselected values from navigation state
    const state = location.state as {
        preselectedMeetingType?: string,
        preselectedMeetingTypeID?: string,
        preselectedProjectName?: string,
        preselectedProjectID?: string,
        preselectedGoalName?: string,
        preselectedGoalID?: string,
        agencyName?: string,
        attendeeFullName?: string,
        attendeeEmail?: string,
        demoRequestId?: string | number,
        demoStartAt?: string,
    } | null;

    const [isToggled, setIsToggled] = useState(false);
    const [showRecurrence, setShowRecurrence] = useState(false);
    const [selectedRecuurence, setSelectedReccurence] = useState("");
    const [recurrenceDays, setRecurrenceDays] = useState([]);
    const [meetingTypeOptions, setMeetingTypeOptions] = useState<
        { key: string; name: string }[]
    >([]);
    const [projectMemberOptions, setProjectMemberOptions] = useState<
        { key: string; name: string; email?: string; uuid?: string }[]
    >([]);
    const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
    const [attendees, setAttendees] = useState<Attendee[]>([]);
    const [projectOptions, setProjectOptions] = useState<{ key: string; name: string }[]>([]);
    const [goalOptions, setGoalOptions] = useState<{ key: string; name: string }[]>([]);
    const [goalMembersMap, setGoalMembersMap] = useState<Record<string, { key: string; name: string }[]>>({});
    const [goalMembers, setGoalMember] = useState<{ key: string; name: string; email?: string }[]>([]);
    const [agendaList, setAgendaList] = useState<{ key: string; name: string }[]>([]);

    // Client Meeting: external Supabase agency/employee lookup
    const [extAgencies, setExtAgencies] = useState<AgencyOption[]>([]);
    const [extAgenciesLoading, setExtAgenciesLoading] = useState(false);
    const [extEmployees, setExtEmployees] = useState<EmployeeOption[]>([]);
    const [extEmployeesLoading, setExtEmployeesLoading] = useState(false);
    const [clientLocationId, setClientLocationId] = useState<string>('');
    const [pendingAttendeesCount, setPendingAttendeesCount] = useState(0);

    // Zoom
    const [zoomMeetingId, setZoomMeetingId] = useState<string>('');
    const [zoomLinkLoading, setZoomLinkLoading] = useState(false);
    const [meetingPlatform, setMeetingPlatform] = useState<string>('');
    const [zoomLinkType, setZoomLinkType] = useState<ZoomLinkType>('support');

    // Validation schema
    const addMeetingSchema = yup.object().shape({
        meetingID: yup.string().required('This field is required'),
        meetingType: yup.string().required('This field is required'),
        meetingTitle: yup
            .string()
            .required("This field is required")
            .test(
                "no-only-spaces",
                "This field is required",
                (value: any) => value && value.trim().length > 0
            ),
        meetingLink: yup
            .string()
            .required("This field is required")
            .matches(
                /^https:\/\/(meet\.google\.com\/[a-zA-Z0-9\-]+|.*zoom\.us\/.*|teams\.microsoft\.com\/.*|[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}(\/[^\s]*)?$)/,
                "Please enter a valid meeting URL."
            ),
        start_date: yup.date().required("This field is required"),
        timePeriod: yup.string().required(""),
        startTimeHour: yup
            .number()
            .typeError("") // Ensures the value is a number
            .required(""),
        startTimeMinute: yup
            .number()
            .typeError("") // Ensures the value is a number
            .required(""),
        durationTimeHour: yup
            .string()
            .typeError("") // Ensures the value is a number
            .required(""),
        durationTimeMinute: yup
            .number()
            .typeError("") // Ensures the value is a number
            .required(""),
        end_date: yup.string(),
        timePeriodEnd: yup.string().required("This field is required"),
        timePeriodEnd24: yup.string(),
        timePeriodStart24: yup.string(),
        remindbefore: isToggled
            ? yup.string().required("This field is required")
            : yup.mixed(),
        is_recurrence: yup.boolean(),
        recurrence_days: yup.mixed(),
        recurrence_end_date: selectedRecuurence.length > 0 && showRecurrence ? yup.date().required("This field is required") : yup.string().nullable(),
        recurrence_repeat_in: yup.mixed(),
        // agenda: yup.string()
        //     .test('not-only-whitespace', 'Please add a valid Goal Description', value => !value || value.trim().length > 0).nullable(),
        hostName: yup.string().required("This field is required"),
        hostID: yup.string(),
        projectName: yup.string(),
        projectID: yup.string(),
        goalName: yup.string(),
        goalID: yup.string(),
        agenda: yup
            .array()
            .of(yup.object())
            .min(1, 'At least one task must be selected'),
        podID: yup.mixed(),
        podName: yup.string().when('meetingType', {
        is: (v: string) => v?.toLowerCase() === 'pod meeting',
        then: (schema) => schema.required('This field is required'),
        otherwise: (schema) => schema,
        }),
        });    

    //api
    const { data: meetingTypesData, loading: meetingTypesDataLoading } = useQuery(MEETING_TYPES, {
        fetchPolicy: 'no-cache'
    });

    const [
        getEmployeesByAgencyID,
        { data: employeesData, loading: employeesDataLoading },
    ] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
        fetchPolicy: 'no-cache'
    });

    const [
        getProjectsByEmpID,
        { data: projectsData, loading: projectsDataLoading },
    ] = useLazyQuery(PROJECTS_BY_EMP_ID, {
        fetchPolicy: 'no-cache'
    });

    const [
        getProjectGoals,
        { data: projectGoals, loading: projectGoalsLoading },
    ] = useLazyQuery(PROJECT_GOALS, {
        fetchPolicy: 'no-cache'
    });

    const [createMeeting, { loading: createMeetingLoading }] = useMutation(CREATE_MEETING, {
        fetchPolicy: 'no-cache',
    })

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        watch,
        control,
        getValues
    } = useForm<any>({
        resolver: yupResolver(addMeetingSchema)
    });

    //watch
    const selected = watch('agenda');
    const watchMeetingID = watch('meetingID')
    const watchMeetingType = watch('meetingType')
    const watchNumericInput1 = watch("startTimeHour");
    const watchNumericInput2 = watch("startTimeMinute");
    const watchNumericInput3 = watch("durationTimeHour");
    const watchNumericInput4 = watch("durationTimeMinute");
    const timeperiod = watch("timePeriod");
    const startDate = watch("start_date");
    const endDate = watch("end_date");
    const endDateTime = watch("timePeriodEnd");
    const watchHostName = watch("hostName");
    const watchProjectName: string = watch('projectName');
    const watchGoalName: string = watch('goalName');
    const isDemoMeeting = watchMeetingType?.toLowerCase() === 'demo meeting';
    const isPodMeeting = watchMeetingType?.toLowerCase() === 'pod meeting';
    const watchPodName: string = watch('podName');

    const podDate = startDate || format(new Date(), 'yyyy-MM-dd');
    const { data: podsData, loading: podsLoading } = useQuery(PODS_DATA_LIST, {
        variables: {
            agency_id: parseInt(organizationID),
            start_date: podDate,
            end_date: podDate,
        },
        skip: !isPodMeeting || !organizationID,
        fetchPolicy: 'no-cache',
    });

    const podOptions: { key: number; name: string }[] =
        podsData?.pods_data?.data?.map((p: any) => ({
            key: p.pod_id,
            name: p.pod_name,
        })).filter((it:any) => it.name != "All Pods") ?? [];

    useEffect(() => {
        const hideThisMeetingTypes = ["Daily Goal Check","Review Meeting","Planning Meeting"]
        if (meetingTypesData) {
            const meetingTypes = meetingTypesData?.meeting_types?.meeting_types
            const options = meetingTypes.map((mt: any) => ({
                key: mt.id,
                name: mt.name
            })).filter((it:any) => !hideThisMeetingTypes.includes(it?.name));
            setMeetingTypeOptions(options)

            // Set preselected meeting type if provided
            if (state?.preselectedMeetingType && state?.preselectedMeetingTypeID) {
                setValue('meetingID', state.preselectedMeetingTypeID)
                setValue('meetingType', state.preselectedMeetingType)
                trigger('meetingType')
                trigger('meetingID')
            } else if (state?.preselectedMeetingType) {
                // Find by name when no ID is provided (e.g., Demo flow)
                const match = options.find(
                    (o: { key: string; name: string }) => o.name.toLowerCase() === state.preselectedMeetingType!.toLowerCase()
                );
                if (match) {
                    setValue('meetingID', match.key)
                    setValue('meetingType', match.name)
                    trigger('meetingType')
                    trigger('meetingID')
                }
            }
        }
    }, [meetingTypesData, state])

    useEffect(() => {
        if (employeeID && watchMeetingType && watchMeetingID == "7") {
            getProjectsByEmpID({
                variables: {
                    emp_id: employeeID,
                    organization_id: organizationID
                },
            });
        }
    }, [employeeID, watchMeetingType])

    useEffect(() => {
        if (projectsData && projectsData.projects_by_emp_id) {
            const projects = projectsData?.projects_by_emp_id || [];
            const options = projects.map((p: any) => ({
                key: p.id,
                name: p.name
            }));
            setProjectOptions(options);

            // Set preselected project if provided
            if (state?.preselectedProjectName && state?.preselectedProjectID) {
                setValue('projectID', state.preselectedProjectID)
                setValue('projectName', state.preselectedProjectName)
                trigger('projectName')

                // Automatically fetch goals for the preselected project
                getProjectGoals({
                    variables: {
                        project_id: parseInt(state.preselectedProjectID),
                        is_archived: false,
                        employee_id: parseInt(employeeID)
                    },
                });
            }
        }
    }, [projectsData, state]);

    useEffect(() => {
        if (organizationID && watchMeetingType && watchMeetingID != "7") {
            getEmployeesByAgencyID({
                variables: {
                    agency_id: parseInt(organizationID)
                }
            });
        }
    }, [organizationID, watchMeetingType])

    // Handle preselected goal when project goals are loaded
    useEffect(() => {
        if (projectGoals && projectGoals.project_goals) {
            const groupedGoals = projectGoals?.project_goals?.grouped_goals ?? [];

            // Flatten into key/name options
            const allGoals = groupedGoals.flatMap((group: any) =>
                group.goals.map((goal: any) => ({
                    key: goal.id,
                    name: goal.title,
                }))
            );

            // Build members map (goalId -> members[])
            const membersMap: Record<string, { key: string; name: string }[]> = {};
            groupedGoals.forEach((group: any) => {
                group.goals.forEach((goal: any) => {
                    membersMap[goal.id] = goal.goal_members.map((member: any) => ({
                        key: member.employee_id.toString(),
                        name: member.member_name,
                    }));
                });
            });

            setGoalOptions(allGoals);
            setGoalMembersMap(membersMap);

            // Set preselected goal if provided
            if (state?.preselectedGoalName && state?.preselectedGoalID) {
                setValue('goalID', state.preselectedGoalID)
                setValue('goalName', state.preselectedGoalName)

                const members = membersMap[state.preselectedGoalID] || [];
                setGoalMember(members)
            }
        }
    }, [projectGoals, state])

    useEffect(() => {
        if (employeesData && employeesData.employees_by_agency_id) {
            const employeeOptions = employeesData?.employees_by_agency_id?.employees?.map((emp: any) => ({
                key: emp.employee_id,
                name: `${emp.first_name} ${emp.last_name}`,
                email: emp.email_id || '',
                uuid: emp.uuid || '',
            }));
            setProjectMemberOptions(employeeOptions);
        }
    }, [employeesData])

    useEffect(() => {
        if (watchMeetingType && watchMeetingID) {
            const selectedMeetingType = meetingTypesData?.meeting_types?.meeting_types.find((meetingType: any) => meetingType.id == watchMeetingID);

            // setValue("taskChecklist", []);

            if (selectedMeetingType) {
                const agendas = selectedMeetingType.agendas.map((a: any) => ({
                    key: parseInt(a.id),
                    name: a.agenda_item
                }));
                setAgendaList(agendas);
                // setClearSelected(false);
            } else {
                setAgendaList([]);
            }
        }
    }, [watchMeetingType]);

    // Auto-fill title and attendees when Demo meeting type is selected
    useEffect(() => {
        if (isDemoMeeting) {
            if (state?.agencyName) {
                setValue('meetingTitle', `Demo for ${state.agencyName}`);
                trigger('meetingTitle');
            }
            if (state?.attendeeFullName && state?.attendeeEmail && attendees.length === 0) {
                setAttendees([{ name: state.attendeeFullName, email: state.attendeeEmail }]);
            }
        }
    }, [watchMeetingType]);

    // Auto-fill start date/time and duration from demoStartAt
    useEffect(() => {
        if (!isDemoMeeting || !state?.demoStartAt) return;
        const parsed = new Date(state.demoStartAt);
        if (isNaN(parsed.getTime())) return;
        const hours24 = parsed.getHours();
        const minutes = parsed.getMinutes();
        const hour12 = hours24 % 12 || 12;
        const period = hours24 < 12 ? 'AM' : 'PM';
        const dateStr = format(parsed, 'yyyy-MM-dd');
        setValue('start_date', dateStr);
        setValue('startTimeHour', hour12);
        setValue('startTimeMinute', minutes);
        setValue('timePeriod', period);
        setValue('durationTimeHour', '00');
        setValue('durationTimeMinute', '30');
    }, [watchMeetingType]);

    useEffect(() => {
        if (watchNumericInput1 > 12) {
            setValue("startTimeHour", 12);
        }
    }, [watchNumericInput1, setValue]);

    useEffect(() => {
        if (watchNumericInput2 > 59) {
            setValue("startTimeMinute", 59);
        }
    }, [watchNumericInput2, setValue]);

    useEffect(() => {
        if (watchNumericInput3 > 4) {
            if (
                watchNumericInput3 == 4 &&
                (watchNumericInput4 == 0 || !watchNumericInput4)
            ) {
                setValue("durationTimeHour", 4);
            } 
            if (
                watchNumericInput3 == 10 &&
                (watchNumericInput4 == 0 || !watchNumericInput4)
            ) {
                setValue("durationTimeHour", 10);
            }else {
                setValue("durationTimeHour", 3);
            }
        }
    }, [watchNumericInput3, setValue]);

    useEffect(() => {
        if (watchNumericInput4 >= 1 && watchNumericInput3 == 36) {
            setValue("durationTimeMinute", 0);
        }
        if (watchNumericInput4 > 59) {
            setValue("durationTimeMinute", 59);
        }
    }, [watchNumericInput4, setValue]);

    useEffect(() => {
        const combineDateAndTime = (dateStr: any, timeStr: any) => {
            // Combine date and time into a single datetime string
            const dateTimeStr = `${dateStr}T${timeStr}`;

            // Parse the datetime string into a Date object
            return parse(dateTimeStr, "yyyy-MM-dd'T'HH:mm", new Date());
        };

        const addDuration = (date: any, hours: any, minutes: any) => {
            // Add hours and minutes to the date
            let newDate = addHours(date, hours);
            newDate = addMinutes(newDate, minutes);
            return newDate;
        };

        if (
            startDate &&
            timeperiod &&
            watchNumericInput1 &&
                   watchNumericInput2 &&
            watchNumericInput3 &&
            watchNumericInput4
        ) {
            let hoursStart;
            let minutesStart;
            let hoursDuration;
            let minutesDuration;
            if (watchNumericInput1 == 0 || watchNumericInput1 == "00") {
                hoursStart = 12;
            } else {
                hoursStart = watchNumericInput1;
            }
            if (watchNumericInput2 > 59) {
                minutesStart = 59;
            } else {
                minutesStart = watchNumericInput2;
            }

            if (watchNumericInput3 == 0 || watchNumericInput3 == "00") {
                hoursDuration = "0";
            } else if (watchNumericInput3 > 36) {
                hoursDuration = 36;
            } else {
                hoursDuration = watchNumericInput3;
            }

            if (watchNumericInput4 > 59) {
                minutesDuration = 59;
            } else {
                minutesDuration = watchNumericInput4;
            }

            const timeDuration = hoursStart + ":" + minutesStart + " " + timeperiod;

            // Combine the date and time into a single string
            const combinedDateTime = `${startDate} ${timeDuration}`;

            // Parse the combined date-time string into a Date object
            const dateTime12Hour = parse(
                combinedDateTime,
                "yyyy-MM-dd hh:mm a",
                new Date()
            );
            // Format the Date object into a 24-hour time string
            const time24Hour = dateTime12Hour ? format(dateTime12Hour, "HH:mm") : "";
            // Combine the date and time
            const combinedDate = combineDateAndTime(startDate, time24Hour);
            // Add the duration
            const newDate = addDuration(combinedDate, hoursDuration, minutesDuration);

            // Format the new date and time separately
            const formattedDate = newDate ? format(newDate, "yyyy-MM-dd") : "";
            const formattedTime = newDate ? format(newDate, "h:mm a") : "";
            const formattedTime24 = newDate ? format(newDate, "HH:mm") : "";
            const formattedStartTime = setValue("end_date", formattedDate);
            setValue("timePeriodEnd", formattedTime);
            setValue("timePeriodEnd24", formattedTime24);
            setValue("timePeriodStart24", time24Hour);
        } else {
            setValue("end_date", null);
            setValue("timePeriodEnd", null);
            setValue("timePeriodEnd24", null);
            setValue("timePeriodStart24", null);
        }
    }, [
        startDate,
        timeperiod,
        watchNumericInput1,
        watchNumericInput2,
        watchNumericInput3,
        watchNumericInput4,
    ]);

    function handleSelectPod(key: number, name: string): void {
    setValue('podID', key)
    setValue('podName', name)
    trigger('podName')
}

    // Fetch active agencies from external Supabase when meeting type is Client Meeting
    const fetchExtAgencies = useCallback(async () => {
        setExtAgenciesLoading(true);
        try {
            const { data, error } = await getSupabaseExt()
                .from('agency')
                .select('agency_id, name, status')
                .eq('status', 'Active');
            if (!error && data) {
                setExtAgencies(
                    data.map((row: any) => ({ id: String(row.agency_id), name: row.name }))
                );
            }
        } finally {
            setExtAgenciesLoading(false);
        }
    }, []);

    // Fetch employees for a selected agency from external Supabase
    const fetchExtEmployees = useCallback(async (agencyId: string) => {
        setExtEmployees([]);
        if (!agencyId) return;
        setExtEmployeesLoading(true);

        // Auto-populate meeting title: "Client Meeting with Agency Name DD-MM-YYYY HH AM/PM"
        const agencyName = extAgencies.find((a) => a.id === agencyId)?.name ?? '';

        // Store the selected agency_id as client_location_id
        setClientLocationId(agencyId);

        if (agencyName) {
            const meetingTypeName = watch('meetingType') || 'Client Meeting';
            const dateStr = startDate
                ? format(new Date(`${startDate}T00:00:00`), 'MM-dd-yyyy')
                : format(new Date(), 'MM-dd-yyyy');
            const timeStr = watch('timePeriodStart24')
                ? format(new Date(`1970-01-01T${watch('timePeriodStart24')}:00`), 'hh a')
                : '';
            const titleParts = [meetingTypeName, 'with', agencyName, dateStr, timeStr].filter(Boolean);
            setValue('meetingTitle', titleParts.join(' '));
            trigger('meetingTitle');
        }

        try {
            const { data, error } = await getSupabaseExt()
                .from('employee')
                .select('id, uuid, agency_id, first_name, middle_name, last_name, email_id, access_type')
                .eq('agency_id', agencyId);
            if (!error && data) {
                setExtEmployees(
                    data.map((emp: any) => {
                        const parts = [
                            emp.last_name,
                            emp.middle_name,
                            emp.first_name,
                        ].filter(Boolean);
                        return {
                            id: String(emp.id),
                            displayName: parts.join(', '),
                            email: emp.email_id || '',
                            accessType: emp.access_type || '',
                            uuid: emp.uuid || '',
                        };
                    })
                );
            }
        } finally {
            setExtEmployeesLoading(false);
        }
    }, [extAgencies, startDate, setValue, trigger]);

    // Load agencies when Client Meeting or Demo Meeting is selected
    useEffect(() => {
        if (watchMeetingType?.toLowerCase() === 'client meeting' || isDemoMeeting) {
            fetchExtAgencies();
        } else {
            setExtAgencies([]);
            setExtEmployees([]);
            setClientLocationId('');
        }
    }, [watchMeetingType, fetchExtAgencies]);

    function onBackClick() {
        navigate(-1)
    }

    async function handleGetZoomLink() {
        const title = watch('meetingTitle');
        if (!title || title.trim().length === 0) {
            showError("Please enter a Meeting Title before generating a Zoom link.");
            return;
        }

        // Build start datetime from form values (fall back to now if not yet set)
        let startISO = '';
        const timePeriodStart24 = watch('timePeriodStart24');
        if (startDate && timePeriodStart24) {
            startISO = `${startDate}T${timePeriodStart24}:00Z`;
        } else if (startDate) {
            startISO = `${startDate}T00:00:00Z`;
        } else {
            startISO = new Date().toISOString();
        }

        const hours = Number(watch('durationTimeHour') || 0);
        const minutes = Number(watch('durationTimeMinute') || 0);
        const durationMinutes = hours * 60 + minutes || 30;

        // Derive agency name from the title "Agency Name : Date" pattern, or use raw title
        const agencyName = title.includes(' : ')
            ? title.split(' : ')[0].trim()
            : title.trim();

        setZoomLinkLoading(true);
        try {
            const result = await createZoomMeeting({
                title: title.trim(),
                agency_name: agencyName,
                start_datetime: startISO,
                duration_minutes: durationMinutes,
                internal_members: selectedMembers.map((m) => ({
                    id: m.key,
                    name: m.name,
                    email: m.email || '',
                })),
                external_members: attendees.map((a) => ({
                    name: a.name,
                    email: a.email,
                    uuid: a.uuid || '',
                    client_location_id: a.client_location_id || clientLocationId || '',
                })),
                host_name: watch('hostName') || '',
                meeting_type: watch('meetingType') || '',
                link_type: zoomLinkType,
                project_name: watch('projectName') || undefined,
                goal_name: watch('goalName') || undefined,
            }, zoomLinkType);

            setValue('meetingLink', result.join_url);
            trigger('meetingLink');
            setZoomMeetingId(result.meeting_id);
            setMeetingPlatform('Zoom');
            showSuccess("Zoom meeting created successfully.");
        } catch (err: any) {
            showError(err?.message || "Failed to create Zoom meeting. Please try again.");
        } finally {
            setZoomLinkLoading(false);
        }
    }

    function handleSelectMeetingType(key: number, name: string): void {
        setValue('meetingID', key)
        setValue('meetingType', name)
        trigger('meetingType')
        trigger('meetingID')

        setAgendaList([])
        setValue("agenda", [])
        setSelectedMembers([]);
        setAttendees([]);
        setValue("hostName", "");
        setValue("hostID", "")
        setValue("podID", "")
        setValue("podName", "")
    }

    // Alphanumeric key allow
    const handleAlphaNumeric = (event: any) => {
        const { key } = event;

        // Allow control keys like backspace, delete, arrows, etc.
        if (
            key === "Backspace" ||
            key === "Delete" ||
            key === "ArrowLeft" ||
            key === "ArrowRight" ||
            key === "Tab" ||
            key === "Enter"
        ) {
            return;
        }

        // Prevent a single space as the only input
        const value = event.target.value;
        if (key === " " && value.trim() === "") {
            event.preventDefault();
            return;
        }

        // // Allow alphanumeric characters, underscores, and spaces
        // if (!/^[a-zA-Z0-9_ ]$/.test(key)) {
        //   event.preventDefault();
        // }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = event;

        // Allow control keys like backspace, delete, arrows, etc.
        if (
            key === "Backspace" ||
            key === "Delete" ||
            key === "ArrowLeft" ||
            key === "ArrowRight" ||
            key === "Tab" ||
            key === "Enter"
        ) {
            return;
        }

        // Prevent non-numeric keys (disallow decimal point)
        if (!/^[0-9]$/.test(key)) {
            event.preventDefault();
        }
    };

    // Numeric only
    const handleInput = (event: any) => {
        const input = event.target.value;
        // Remove any non-numeric characters (disallow letters and special characters)
        event.target.value = input.replace(/[^0-9]/g, "");
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const paste = event.clipboardData.getData("text");
        if (!/^\d+$/.test(paste)) {
            event.preventDefault();
        }
    };

    function handleSelectTime(key: any, value?: any) {
        setValue("timePeriod", value);
        trigger("timePeriod");
    }

    const handleRecurrenceChange = () => {
        if (showRecurrence) {
            setSelectedReccurence("");
        } else {
            setSelectedReccurence("daily");
        }
        setShowRecurrence((prev) => !prev);
    };

    const handleRadioChange = (event: any) => {
        setSelectedReccurence(event.target.value);
        setRecurrenceDays([]);
        setValue("recurrence_end_date", null);
        setValue("recurrence_repeat_in", "");
        setValue("recurrence_days", "");
    };

    const handleRecurrenceDays = (value: any) => {
        setRecurrenceDays((prevDays: any) => {
            // Check if the value is already in the array
            if (prevDays.includes(value)) {
                // If it is, remove it
                return prevDays.filter((day: any) => day !== value);
            } else {
                // If it isn't, add it
                return [...prevDays, value];
            }
        });
    };

    // Function to handle toggle action
    const handleToggle = () => {
        setIsToggled((prev) => !prev);
    };

    function handleRemindBefore(key: any, value?: any) {
        setValue("remindbefore", value);
        trigger("remindbefore");
    }

    const handleSelectMembers = (key: any, name: any, type: any) => {
        const allOptions = watchMeetingID == "7" ? goalMembers : projectMemberOptions;

        if (key === "all") {
            if (name === true) {
                setSelectedMembers([]);
            } else {
                setSelectedMembers(allOptions);
            }
        } else {
            setSelectedMembers((prev) => {
                const exists = prev.some((item) => item.key == key);
                if (exists) {
                    return prev.filter((item) => item.key != key);
                } else {
                    // Carry email and uuid from the source options list
                    const sourceOption = (allOptions ?? []).find((o) => o.key == key);
                    return [...prev, { key, name, type, email: sourceOption?.email || '', uuid: (sourceOption as any)?.uuid || '' }];
                }
            });
        }
    };

    function handleSelectHost(key: number, name: string): void {
        setValue('hostName', name)
        setValue('hostID', key)
        trigger('hostName')
    }

    async function handleSelectProject(key: string, name: string): Promise<void> {
        setValue('projectID', key)
        setValue('projectName', name)
        trigger('projectName')

        const response = await getProjectGoals({
            variables: {
                project_id: parseInt(key),
                is_archived: false,
                employee_id: parseInt(employeeID)
            },
        });

        // Safely extract grouped goals
        const groupedGoals = response?.data?.project_goals?.grouped_goals ?? [];

        // Flatten into key/name options
        const allGoals = groupedGoals.flatMap((group: any) =>
            group.goals.map((goal: any) => ({
                key: goal.id,
                name: goal.title,
            }))
        );

        // Build members map (goalId -> members[])
        const membersMap: Record<string, { key: string; name: string }[]> = {};
        groupedGoals.forEach((group: any) => {
            group.goals.forEach((goal: any) => {
                membersMap[goal.id] = goal.goal_members.map((member: any) => ({
                    key: member.employee_id.toString(),
                    name: member.member_name,
                }));
            });
        });

        setGoalOptions(allGoals);
        setGoalMembersMap(membersMap);
    }

    function handleSelectGoal(key: number, name: string) {
        setValue('goalID', key)
        setValue('goalName', name)

        const members = goalMembersMap[key] || [];
        setGoalMember(members)
        // set into another state if you want for member dropdown
    };

    console.log(errors,'errors')

    async function onSubmit(data: any) {
        const isStandupMeeting = watchMeetingType?.toLowerCase().includes('standup') ||
            watchMeetingType?.toLowerCase().includes('stand-up') ||
            watchMeetingType?.toLowerCase().includes('stand up');

        if (!isStandupMeeting && (selectedMembers.length + attendees.length) < 2) {
            showError("Please select at least two members for the meeting.");
            return;
        }

        // Client/Demo meeting must have external attendees committed via "Add Selected"
        const isClientMeeting = watchMeetingType?.toLowerCase() === 'client meeting' || isDemoMeeting;
        if (isClientMeeting && attendees.length === 0) {
            if (pendingAttendeesCount > 0) {
                showError(`You have ${pendingAttendeesCount} employee${pendingAttendeesCount > 1 ? 's' : ''} selected but not added. Please click "Add Selected" before submitting.`);
            } else {
                showError("Please add at least one external attendee.");
            }
            return;
        }

        if (selected?.length < 1) {
            showError("Please make Agenda for the meeting.");
            return;
        }

        const is_recurrence = showRecurrence
        const recurrence = showRecurrence ? selectedRecuurence : null
        const recurrence_days = showRecurrence
            ? recurrenceDays?.length > 0
                ? recurrenceDays
                : ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
            : null
        const recurrence_repeat_in = Number(data?.recurrence_repeat_in)
        const recurrence_end_date =
            showRecurrence && selectedRecuurence?.length > 0 ? format(new Date(data?.recurrence_end_date), "yyyy-MM-dd") : null

        // Get current date and time
        const now = new Date();
        const selectedDate = new Date(startDate);

        // Convert selected time to 24-hour format
        let hour = parseInt(data?.startTimeHour);
        const minute = parseInt(data?.startTimeMinute);

        // Convert to 24-hour format
        if (data?.timePeriod?.toLowerCase() === "pm" && hour !== 12) {
            hour += 12;
        } else if (data?.timePeriod?.toLowerCase() === "am" && hour === 12) {
            hour = 0;
        }

        // Set the selected time on the selected date
        selectedDate.setHours(hour, minute, 0, 0);

        // Compare with current time
        // if (selectedDate <= now) {
        //     showError(
        //         "Cannot schedule meeting in the past. Please select a future time."
        //     );
        //     return;
        // }

        const startMeetDate = `${startDate}T${watch("timePeriodStart24")}:00Z`;
        const endMeetDateTime = `${endDate}T${watch("timePeriodEnd24")}:00Z`;

        const meetingHours = Number(data?.durationTimeHour);
        const meetingMinutes = Number(data?.durationTimeMinute);

        // Validation: Check if both hours and minutes are 0
        if (meetingHours === 0 && meetingMinutes === 0) {
            showError("Please add a valid Duration");
            return; // Prevent further execution and API call
        }

        const membersList = selectedMembers.map((member) => ({
            member_id: Number(member.key),
            is_attended: true,
            absent_note: null
        }))

        const externalMembersList = attendees.map((member) => ({
            name: member.name,
            email: member.email,
            is_attended: true,
            uuid: member.uuid || null,
            client_location_id: (member.client_location_id || clientLocationId)
                ? parseInt(member.client_location_id || clientLocationId)
                : null,
        }))

        const agendaList = selected.map((a: any) => ({
            agenda_id: a?.key,
            title: a?.name,
            is_discussed: false
        }))

        const recurrenceDetails = {
            recurrence_type: recurrence,
            recurrence_start_date: startDate,
            week_days: recurrence_days,
            skip_value: recurrence_repeat_in,
            recurrence_end_date: recurrence_end_date
        }
        
        console.log(data,'data')
        
        const input = {
            meeting_type_id: parseInt(data?.meetingID),
            agency_id: parseInt(organizationID),
            title: data?.meetingTitle,
            start_datetime: startMeetDate,
            end_datetime: endMeetDateTime,
            host_user_id: parseInt(data?.hostID),
            status_id: 1,
            meeting_link_string: data?.meetingLink,
            meeting_link_html: `<a href=${data?.meetingLink}>Join Meeting</a>`,
            event_type_text: data?.meetingType,
            is_email_send: false,
            remind_time: 15,
            duration: String(meetingHours * 60 + meetingMinutes),
            is_recurrence: is_recurrence,
            created_by: parseInt(employeeID),
            members_list: membersList,
            external_members_list: externalMembersList,
            agenda_items: agendaList,
            project: data?.projectID ? parseInt(data?.projectID) : null,
            goal: data?.goalID ? parseInt(data?.goalID) : null,
            recurrence_details: is_recurrence === true ? recurrenceDetails : null,
            // zoom_meeting_id: zoomMeetingId || null,
            meeting_platform: meetingPlatform || null,
            meeting_platform_id: zoomMeetingId || null,
            pod_id:data?.podID ? data?.podID : null
        }

        try {
            const response = await createMeeting({ variables: { input: input } });
            if (response.data.create_meeting.status_code === 201) {
                showSuccess(response.data.create_meeting.message)

                // Fire secondary API call to caryfy employee endpoint for client/demo meetings
                if (watchMeetingType?.toLowerCase() === 'client meeting' || isDemoMeeting) {
                    try {
                        // Build members_ids: uuid of host + all internal members + external attendees
                        const hostUuid = selectedMembers.find(
                            (m) => String(m.key) === String(data?.hostID)
                        )?.uuid || (userInfo?.uuid ?? '');

                        const externalUuids: string[] = attendees
                            .map((a: any) => a.uuid)
                            .filter(Boolean);

                        const allMemberIds = Array.from(
                            new Set([...externalUuids].filter(Boolean))
                        );

                        const caryfyPayload = {
                            operationName: "CreateMeeting",
                            variables: {
                                description: "",
                                end_date: endMeetDateTime,
                                is_reminder: false,
                                meeting_host_uuid: hostUuid,
                                meeting_hours: meetingHours,
                                meeting_minutes: meetingMinutes,
                                reminder: "",
                                start_date: startMeetDate,
                                title: data?.meetingTitle,
                                members_ids: allMemberIds,
                                meeting_url: data?.meetingLink || "",
                            },
                            query: `mutation CreateMeeting($description: String!, $end_date: DateTime!, $is_reminder: Boolean!, $meeting_host_uuid: String!, $meeting_hours: Int!, $meeting_minutes: Int!, $reminder: String!, $start_date: DateTime!, $title: String!, $members_ids: [String!]!, $meeting_url: String!) {\n  create_meeting(\n    description: $description\n    end_date: $end_date\n    is_reminder: $is_reminder\n    meeting_host_uuid: $meeting_host_uuid\n    meeting_hours: $meeting_hours\n    meeting_minutes: $meeting_minutes\n    reminder: $reminder\n    start_date: $start_date\n    title: $title\n    members_ids: $members_ids\n    meeting_url: $meeting_url\n  ) {\n    status\n    status_code\n    message\n    meeting {\n      id\n      title\n      start_date\n      end_date\n      meeting_host_uuid\n      status\n      meeting_url\n      created_on\n      description\n      __typename\n    }\n    __typename\n  }\n}`,
                        };

                        const token = localStorage.getItem("authToken");
                        await fetch("https://caryfyprodapi.caryfy.ai/caryfy/employee/", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: token ? `Bearer ${token}` : "",
                            },
                            body: JSON.stringify(caryfyPayload),
                        });
                        // We don't block navigation on this call — fire and continue
                    } catch (_) {
                        // Secondary call failure is non-blocking
                    }
                }

                // Update demo request row in Supabase with meeting details
                if (isDemoMeeting && state?.demoRequestId) {
                    try {
                        await getSupabaseExt()
                            .from('carebravosite_contact_demo_requests')
                            .update({
                                meeting_detail: {
                                    start_datetime: startMeetDate,
                                    end_datetime: endMeetDateTime,
                                    title: data?.meetingTitle,
                                    meeting_id:response.data.create_meeting.meeting_id,
                                    zoom_meeting_detail:{
                                        id:zoomMeetingId,
                                        link:data?.meetingLink
                                    }
                                }
                            })
                            .eq('id', state.demoRequestId);
                    } catch (_) {
                        // Non-blocking
                    }
                }

                navigate(-1)
            }
            if (response.data.create_meeting.status_code === 400) {
                showError(response.data.create_meeting.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }

    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} className="w-full relative overflow-hidden flex flex-col items-start justify-start min-w-full max-w-full leading-[normal] tracking-[normal] text-left text-xl text-text-text-primary font-body-reg mq950:max-w-full mq450:max-w-full mq1425:max-w-full mq1900:max-w-full">
                <div id="cts-respon-p-dv" className=" self-stretch overflow-hidden flex flex-row flex-wrap items-start justify-center py-40 px-20 mq575:px-5 box-border max-w-full mq950:pt-[34px] mq950:pb-[34px] mq950:box-border mq1425:box-border">
                    <div className="w-full flex flex-col items-start justify-start gap-[24px] max-w-full mq1425:max-w-full">
                        <div className="grid gap-4 w-full">
                         <BreadCumb />
                        <div className="self-stretch flex flex-row items-center justify-start gap-[5px] max-w-full">
                            <img
                                className="h-6 w-6 relative overflow-hidden shrink-0  dark-white-img cursor-pointer"
                                loading="lazy"
                                alt=""
                                src={dftarrowback}
                                onClick={onBackClick}
                            />
                            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base">
                                Add New Meeting
                            </h3>
                        </div>
                       </div>
                        <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                            {/* <h5 className="my-0 text-xl-dv self-stretch relative text-lg">Goal Details</h5> */}
                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                                <SearchableInputsDropdown
                                    label="Meeting Type*"
                                    initialValue={watchMeetingType}
                                    placeHolder="Select meeting type"
                                    options={meetingTypeOptions}
                                    onSelect={handleSelectMeetingType}
                                    errorMessage={errors?.meetingType}
                                />
                            </div>

                            {/* For Client/Demo Meeting: ordered as Date/Time → Internal Members → External Attendees → Title/Link */}
                            {watchMeetingType?.toLowerCase() === 'client meeting' || isDemoMeeting ? (
                                <>
                                    {/* 1. Date / Time / Duration */}
                                    <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-4 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full items-start">
                                        <Controller
                                            name="start_date"
                                            control={control}
                                            render={({ field }) => (
                                                <DateSelector
                                                    label="Start Date*"
                                                    selectedDate={field.value}
                                                    onChange={(date: string | null) => {
                                                        field.onChange(date);
                                                        setValue("start_date", date);
                                                        trigger("start_date");
                                                    }}
                                                    nothideParsedValue={true}
                                                    blueIcon={true}
                                                    error={errors.start_date?.message}
                                                    showAllDate={true}
                                                />
                                            )}
                                        />
                                        <div className="flex items-start justify-start gap-[8px]">
                                            <div className="flex-1 flex flex-col items-start justify-start">
                                                <InputsNew
                                                    label={"Time*"}
                                                    contentPlaceholder={"HH"}
                                                    type="number"
                                                    propMinWidth="0"
                                                    maxValue={2}
                                                    onKeyDown={handleKeyDown}
                                                    onInput={handleInput}
                                                    onPaste={handlePaste}
                                                    register={{ ...register("startTimeHour") }}
                                                    errorMessage1={errors.startTimeHour}
                                                    autoComplete={"off"}
                                                />
                                            </div>
                                            <div className="flex-1 flex flex-col items-start justify-start pt-5">
                                                <InputsNew
                                                    label={""}
                                                    contentPlaceholder={"MM"}
                                                    type="number"
                                                    maxValue={2}
                                                    onKeyDown={handleKeyDown}
                                                    onInput={handleInput}
                                                    onPaste={handlePaste}
                                                    register={{ ...register("startTimeMinute") }}
                                                    errorMessage1={errors.startTimeMinute}
                                                    autoComplete={"off"}
                                                />
                                            </div>
                                            <div className="flex-1 flex flex-col items-start justify-start pt-5">
                                                <InputsDropdown
                                                    showSearchable={true}
                                                    label={""}
                                                    placeHolder={"Select"}
                                                    options={[{ key: "am", name: "AM" }, { key: "pm", name: "PM" }]}
                                                    onSelect={handleSelectTime}
                                                    errorMessage1={errors.timePeriod}
                                                    initialValue={watch('timePeriod')}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-start justify-start gap-[8px]">
                                            <div className="flex-1 flex flex-col items-start justify-start">
                                                <InputsNew
                                                    label={"Duration*"}
                                                    contentPlaceholder={"HH"}
                                                    type="number"
                                                    maxValue={2}
                                                    onKeyDown={handleKeyDown}
                                                    onInput={handleInput}
                                                    onPaste={handlePaste}
                                                    register={{ ...register("durationTimeHour") }}
                                                    errorMessage1={errors.durationTimeHour}
                                                    autoComplete={"off"}
                                                />
                                            </div>
                                            <div className="flex-1 flex flex-col items-start justify-start pt-5">
                                                <InputsNew
                                                    label={""}
                                                    contentPlaceholder={"MM"}
                                                    type="number"
                                                    maxValue={2}
                                                    onKeyDown={handleKeyDown}
                                                    onInput={handleInput}
                                                    onPaste={handlePaste}
                                                    register={{ ...register("durationTimeMinute") }}
                                                    errorMessage1={errors.durationTimeMinute}
                                                    autoComplete={"off"}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2. Internal Members */}
                                    {watchMeetingID != 7 && (
                                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
                                            <div className="grid gap-[16px]">
                                                <InputsDropdownMultipleSearchSelect
                                                    label="Select Internal Members*"
                                                    placeHolder="Choose"
                                                    options={projectMemberOptions}
                                                    onSelect={handleSelectMembers}
                                                    inputType="checkbox"
                                                    checkedItems={selectedMembers}
                                                />
                                                <div className="reportmulselect-blg cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                                                    <p className="cursor-pointer" onClick={() => setShowMembers(true)}>
                                                        View Members ({selectedMembers?.length})
                                                    </p>
                                                </div>
                                            </div>
                                            {selectedMembers?.length > 0 && (
                                                <SearchableInputsDropdown
                                                    label="Select Host*"
                                                    initialValue={watchHostName}
                                                    placeHolder="Select"
                                                    options={selectedMembers}
                                                    onSelect={handleSelectHost}
                                                    errorMessage={errors?.hostName}
                                                />
                                            )}
                                        </div>
                                    )}

                                    {/* 3. External Attendees (Agency + employees) */}
                                    <div className="self-stretch flex flex-col items-start justify-start text-sm">
                                        <ExternalAttendees
                                            value={attendees}
                                            onChange={setAttendees}
                                            isSingle={false}
                                            isClientMeeting={true}
                                            agencies={extAgencies}
                                            agenciesLoading={extAgenciesLoading}
                                            onAgencySelect={fetchExtEmployees}
                                            employees={extEmployees}
                                            employeesLoading={extEmployeesLoading}
                                            onPendingCountChange={setPendingAttendeesCount}
                                            isDemoMeeting={isDemoMeeting}
                                        />
                                    </div>

                                    {/* 4. Meeting Title + Link + Get Zoom Link */}
                                    <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
                                        <InputsNew
                                            contentPlaceholder="Meeting Title"
                                            label="Meeting Title*"
                                            type="text"
                                            register={register('meetingTitle')}
                                            errorMessage={errors.meetingTitle}
                                        />
                                        <div className="flex flex-col items-start justify-start gap-1">
                                            <InputsNew
                                                contentPlaceholder="Meeting Link *"
                                                label="Meeting Link *"
                                                type="text"
                                                register={register('meetingLink')}
                                                errorMessage={errors.meetingLink}
                                            />
                                            <div className="flex items-center gap-4">
                                                {(['support', 'marketing','hello'] as ZoomLinkType[]).map((type) => (
                                                    <label key={type} className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-text-text-primary dark:text-white">
                                                        <input
                                                            type="radio"
                                                            name="zoomLinkType"
                                                            value={type}
                                                            checked={zoomLinkType === type}
                                                            onChange={() => setZoomLinkType(type)}
                                                            className="cursor-pointer"
                                                        />
                                                        {type.charAt(0).toUpperCase() + type.slice(1)} Link
                                                    </label>
                                                ))}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleGetZoomLink}
                                                disabled={
                                                    zoomLinkLoading ||
                                                    !watch('meetingTitle')?.trim() ||
                                                    !startDate ||
                                                    !watch('timePeriodStart24')
                                                }
                                                className="cursor-pointer flex items-center gap-[6px] py-1.5 px-3 rounded-md text-xs font-semibold bg-surface-primary-surface text-white hover:bg-dodgerblue disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {zoomLinkLoading ? (
                                                    <>
                                                        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                        </svg>
                                                        Generating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                                                            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                                                        </svg>
                                                        Get Zoom Link
                                                    </>
                                                )}
                                            </button>
                                            {zoomMeetingId && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    Zoom Meeting ID: <span className="font-semibold">{zoomMeetingId}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                /* Non-client meeting: original date/time + title/link order */
                                <>
                                    <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-4 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full items-start">
                                <Controller
                                    name="start_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="Start Date*"
                                            selectedDate={field.value}
                                            onChange={(date: string | null) => {
                                                field.onChange(date);
                                                setValue("start_date", date);
                                                trigger("start_date");
                                            }}
                                            nothideParsedValue={true}
                                            blueIcon={true}
                                            error={errors.start_date?.message}
                                            showAllDate={true}
                                        />
                                    )}
                                />
                                <div className="flex items-start justify-start gap-[8px]">
                                    <div className="flex-1 flex flex-col items-start justify-start">
                                        <InputsNew
                                            label={"Time*"}
                                            contentPlaceholder={"HH"}
                                            type="number"
                                            propMinWidth="0"
                                            maxValue={2}
                                            onKeyDown={handleKeyDown}
                                            onInput={handleInput}
                                            onPaste={handlePaste}
                                            register={{ ...register("startTimeHour") }}
                                            errorMessage1={errors.startTimeHour}
                                            autoComplete={"off"}
                                        />
                                    </div>
                                    <div className="flex-1 flex flex-col items-start justify-start pt-5">
                                        <InputsNew
                                            label={""}
                                            contentPlaceholder={"MM"}
                                            type="number"
                                            maxValue={2}
                                            onKeyDown={handleKeyDown}
                                            onInput={handleInput}
                                            onPaste={handlePaste}
                                            register={{ ...register("startTimeMinute") }}
                                            errorMessage1={errors.startTimeMinute}
                                            autoComplete={"off"}
                                        />
                                    </div>
                                    <div className="flex-1 flex flex-col items-start justify-start pt-5">
                                        <InputsDropdown
                                            showSearchable={true}
                                            label={""}
                                            placeHolder={"Select"}
                                            options={[
                                                {
                                                    key: "am",
                                                    name: "AM",
                                                },
                                                {
                                                    key: "pm",
                                                    name: "PM",
                                                },
                                            ]}
                                            onSelect={handleSelectTime}
                                            errorMessage1={errors.timePeriod}
                                        // clearSelected={clearSelected}
                                        />
                                    </div>
                                </div>

                                <div className="flex items-start justify-start gap-[8px]">
                                    <div className="flex-1 flex flex-col items-start justify-start ">
                                        <InputsNew
                                            label={"Duration*"}
                                            contentPlaceholder={"HH"}
                                            type="number"
                                            maxValue={2}
                                            onKeyDown={handleKeyDown}
                                            onInput={handleInput}
                                            onPaste={handlePaste}
                                            register={{ ...register("durationTimeHour") }}
                                            errorMessage1={errors.durationTimeHour}
                                            autoComplete={"off"}
                                        />
                                    </div>

                                    <div className="flex-1 flex flex-col items-start justify-start pt-5">
                                        <InputsNew
                                            label={""}
                                            contentPlaceholder={"MM"}
                                            type="number"
                                            maxValue={2}
                                            onKeyDown={handleKeyDown}
                                            onInput={handleInput}
                                            onPaste={handlePaste}
                                            register={{ ...register("durationTimeMinute") }}
                                            errorMessage1={errors.durationTimeMinute}
                                            autoComplete={"off"}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Meeting Title and Link — non-client meeting */}
                            {(() => {
                                const isStandup = watchMeetingType?.toLowerCase().includes('standup') ||
                                    watchMeetingType?.toLowerCase().includes('stand-up') ||
                                    watchMeetingType?.toLowerCase().includes('stand up');                                    
                                return (
                                    <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                                        <InputsNew
                                            contentPlaceholder="Meeting Title"
                                            label="Meeting Title*"
                                            type="text"
                                            register={register('meetingTitle')}
                                            errorMessage={errors.meetingTitle}
                                        />
                                        {isPodMeeting && (
                                            <SearchableInputsDropdown
                                                label="Pod*"
                                                initialValue={watchPodName}
                                                placeHolder="Select Pod"
                                                options={podOptions}
                                                onSelect={handleSelectPod}
                                                errorMessage={errors?.podName}
                                            />
                                        )}
                                       
                                            <div className="flex flex-col items-start justify-start gap-1">
                                                <InputsNew
                                                    contentPlaceholder="Meeting Link *"
                                                    label="Meeting Link *"
                                                    type="text"
                                                    register={register('meetingLink')}
                                                    errorMessage={errors.meetingLink}
                                                />
                                                <div className="flex items-center gap-4">
                                                    {(['support', 'marketing','hello'] as ZoomLinkType[]).map((type) => (
                                                        <label key={type} className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-text-text-primary dark:text-white">
                                                            <input
                                                                type="radio"
                                                                name="zoomLinkType"
                                                                value={type}
                                                                checked={zoomLinkType === type}
                                                                onChange={() => setZoomLinkType(type)}
                                                                className="cursor-pointer"
                                                            />
                                                            {type.charAt(0).toUpperCase() + type.slice(1)} Link
                                                        </label>
                                                    ))}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleGetZoomLink}
                                                    disabled={
                                                        zoomLinkLoading ||
                                                        !watch('meetingTitle')?.trim() ||
                                                        !startDate ||
                                                        !watch('timePeriodStart24')
                                                    }
                                                    className="cursor-pointer flex items-center gap-[6px] py-1.5 px-3 rounded-md text-xs font-semibold bg-surface-primary-surface text-white hover:bg-dodgerblue disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    {zoomLinkLoading ? (
                                                        <>
                                                            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                            </svg>
                                                            Generating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                                                                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                                                            </svg>
                                                            Get Zoom Link
                                                        </>
                                                    )}
                                                </button>
                                                {zoomMeetingId && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        Zoom Meeting ID: <span className="font-semibold">{zoomMeetingId}</span>
                                                    </p>
                                                )}
                                            </div>
                                  
                                 
                                    </div>
                                );
                            })()}
                                </>
                            )}

                            {!isDemoMeeting && (
                            <div className="self-stretch flex flex-row items-start justify-start gap-6">
                                <div className="flex flex-col items-start justify-center gap-[8px]">
                                    <div className="flex flex-row items-center justify-start gap-[6px]">
                                        <input
                                            className="m-0 h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                                            type="checkbox"
                                            onClick={handleRecurrenceChange}
                                            id="recurrenceCheckBox"
                                            checked={showRecurrence}
                                        />
                                        <label
                                            htmlFor="recurrenceCheckBox"
                                            className={`cursor-pointer relative text-sm font-body-reg dark:text-white  text-left inline-block min-w-[76px] ${showRecurrence ? "text-surface-primary-surface" : "text-colors-text-black"
                                                }`}
                                        >
                                            Recurrence
                                        </label>
                                    </div>
                                </div>
                            </div>
                            )}

                            {!isDemoMeeting && showRecurrence && (
                                <div className=" self-stretch rounded-radius-md1 flex flex-col items-start justify-start box-border gap-[16px] max-w-full range-recurrence-dv">
                                    <div className="self-stretch flex flex-col items-start justify-start gap-[16px] ">
                                        <b className="self-stretch relative text-sm font-body-reg text-text-text-primary text-left dark:text-white">Range and Recurrence:</b>
                                        <div className="w-full flex flex-row items-start justify-start gap-[16px] max-w-[623px] mq950:flex-wrap mq950:max-w-full">
                                            {["daily", "weekly"].map((option) => {
                                                const checked = selectedRecuurence === option;

                                                return (
                                                    <div key={option} className="flex flex-row items-center justify-start gap-[6px]">
                                                        <input
                                                            className="cursor-pointer m-0 h-4 w-4 relative overflow-hidden shrink-0"
                                                            type="radio"
                                                            name="radio1"
                                                            value={option}
                                                            onChange={handleRadioChange}
                                                            defaultChecked={option === "daily"}
                                                            id={option}
                                                        />
                                                        <label
                                                            htmlFor={option}
                                                            className={`cursor-pointer relative text-sm font-body-reg  text-left inline-block dark:text-white ${checked ? "text-surface-primary-surface" : "text-text-text-primary"
                                                                } min-w-[${option.length * 8}px]`}
                                                        >
                                                            {option.charAt(0).toUpperCase() + option.slice(1).replace("-", " ")}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {selectedRecuurence && (
                                        <div className="self-stretch flex flex-col items-start justify-start gap-[10px]">
                                            {["weekly"].includes(selectedRecuurence) && (
                                                <div className="self-stretch flex flex-row flex-wrap items-start justify-start max-w-full">
                                                    <div className="flex-1 flex flex-row flex-wrap items-center justify-start gap-[16px] max-w-full mq575:grid">
                                                        {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                                                            const checked = recurrenceDays.find((it: any) => it === day);
                                                            return (
                                                                <div key={day} className="flex-1 flex flex-row items-center justify-start gap-[6px]">
                                                                    <input
                                                                        className="m-0 h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                                                                        type="checkbox"
                                                                        onClick={() => handleRecurrenceDays(day)}
                                                                        id={day}
                                                                    />
                                                                    <label
                                                                        htmlFor={day}
                                                                        className={`cursor-pointer relative text-sm font-body-reg dark:text-white text-left ${checked ? "text-surface-primary-surface" : "text-text-text-primary"
                                                                            } inline-block min-w-[52px]`}
                                                                    >
                                                                        {day}
                                                                    </label>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            {/* <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                                                {selectedRecuurence === "Days" && (
                                                    <InputsNew
                                                        label={"Repeat In Days"}
                                                        contentPlaceholder={"0"}
                                                        type="number"
                                                        maxValue={2}
                                                        onKeyDown={handleKeyDown}
                                                        onPaste={handlePaste}
                                                        register={{ ...register("recurrence_repeat_in") }}
                                                        autoComplete={"off"}
                                                    />
                                                )}
                                            </div> */}
                                            {/* <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                                                {selectedRecuurence === "Monthly" && (
                                                    <InputsNew
                                                        label={"Repeat In Months"}
                                                        contentPlaceholder={"0"}
                                                        type="number"
                                                        maxValue={2}
                                                        onKeyDown={handleKeyDown}
                                                        onPaste={handlePaste}
                                                        register={{ ...register("recurrence_repeat_in") }}
                                                        autoComplete={"off"}
                                                    />
                                                )}
                                            </div> */}
                                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                                                {selectedRecuurence === "weekly" && (
                                                    <InputsNew
                                                        label={"Repeat In Weeks"}
                                                        contentPlaceholder={"0"}
                                                        type="number"
                                                        maxValue={2}
                                                        onKeyDown={handleKeyDown}
                                                        onPaste={handlePaste}
                                                        register={{ ...register("recurrence_repeat_in") }}
                                                        autoComplete={"off"}
                                                    />
                                                )}
                                            </div>

                                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                                                <Controller
                                                    name="start_date"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DateSelector
                                                            label="Start Date*"
                                                            selectedDate={field.value}
                                                            onChange={(date: string | null) => {
                                                                field.onChange(date);
                                                                setValue("start_date", date);
                                                                trigger("start_date");
                                                            }}
                                                            nothideParsedValue={true}
                                                            blueIcon={false}
                                                            dontopenDatepicker={true}
                                                            error={errors.start_date?.message}
                                                            type="shift"
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name="recurrence_end_date"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DateSelector
                                                            label="End Date*"
                                                            selectedDate={field.value}
                                                            onChange={(date: string | null | any) => {
                                                                const date2 = parseISO(watch("start_date"));
                                                                const date1 = parseISO(date);

                                                                const daysDifference = differenceInDays(date1, date2);

                                                                if (isBefore(date1, date2)) {
                                                                    showError("Please select date greater than start date.");
                                                                } else if (selectedRecuurence === "daily") {
                                                                    if (daysDifference <= 30) {
                                                                        field.onChange(date);
                                                                        setValue("recurrence_end_date", date);
                                                                        trigger("recurrence_end_date");
                                                                    } else {
                                                                        showError("For daily recurrence, end date cannot be more than 30 days after the start date.");
                                                                    }
                                                                } else if (selectedRecuurence === "weekly") {
                                                                    const daysDifference = differenceInDays(date, watch("start_date"));
                                                                    if (daysDifference > 6) {
                                                                        field.onChange(date);
                                                                        setValue("recurrence_end_date", date);
                                                                        trigger("recurrence_end_date");
                                                                    } else {
                                                                        showError("Please add repeat until date at least 7 days ahead of from date.");
                                                                    }
                                                                } else {
                                                                    field.onChange(date);
                                                                    setValue("recurrence_end_date", date);
                                                                    trigger("recurrence_end_date");
                                                                }

                                                            }}
                                                            nothideParsedValue={true}
                                                            blueIcon={true}
                                                            dontopenDatepicker={!watch("start_date")}
                                                            // minDate={watch("start_date")}
                                                            error={errors.recurrence_end_date?.message}
                                                            showAllDate={true}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-4 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                                <div className="flex-1 flex flex-row items-center justify-start flex-wrap content-center py-0 pl-0 box-border gap-x-2.5 gap-y-4 readio-tuggle-blg-dv">
                                    <b className="flex-1 relative text-base inline-block font-body-xs-semi-bold text-colors-text-black text-left min-w-[110px] dark:text-white">
                                        Set reminder
                                    </b>

                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={isToggled}
                                            onChange={handleToggle}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            </div>
                            {isToggled == true && (
                                <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-4 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                                    <InputsDropdown
                                        label={"Remind Members Before"}
                                        placeHolder={"Select"}
                                        options={[
                                            {
                                                key: "15_minutes",
                                                name: "15 minutes",
                                            },
                                            {
                                                key: "30_minutes",
                                                name: "30 minutes",
                                            },
                                            {
                                                key: "1_hours",
                                                name: "1 hours",
                                            },
                                        ]}
                                        onSelect={handleRemindBefore}
                                        errorMessage={errors.remindbefore}
                                    // clearSelected={clearSelected}
                                    />
                                </div>
                            )} */}

                            {/* Meeting Agenda */}

                            <div className="w-full grid ">
                                {/* <ExternalAttendees value={agenda} onChange={setAgenda} isSingle={true} /> */}
                                {agendaList && agendaList.length > 0 && (
                                    <div className="grid gap-[16px] checklist-section-dv border-surface-border-blg-dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                                        {/* <div className="self-stretch flex flex-row items-center justify-start flex-col py-0 pr-0 mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border gap-[8px]">
                                            <div className="self-stretch flex items-center justify-start py-0  gap-[8px] ">
                                                <h6 className="my-0 relative dark:text-white whitespace-pre-wrap"><b>Select Agenda*</b></h6>
                                                <span className="checkliste-selected-count">{selected?.length}</span>
                                            </div>
                                            <div className="flex min-w-full selectall_header_bg">
                                                <input
                                                    type="checkbox"
                                                    id="selectAllCheckbox"
                                                    checked={agendaList.length > 0 && agendaList.every(agenda => control._formValues.agendaList?.includes(agenda.key))}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            handleSelectAll();
                                                        } else {
                                                            handleDeselectAll();
                                                        }
                                                    }}
                                                />
                                                <label htmlFor="selectAllCheckbox" className="font-semibold">Select All</label>
                                            </div>
                                        </div> */}
                                        <div className="self-stretch flex flex-row items-center justify-start flex-col py-0 pr-0 mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border gap-[8px]">
                                            <div className="self-stretch flex items-center justify-start py-0  gap-[8px] ">
                                                <h6 className="my-0 relative dark:text-white whitespace-pre-wrap">
                                                    <b>Select Agenda*</b>
                                                </h6>
                                                <span className="checkliste-selected-count">{control._formValues.agenda?.length || 0}</span>
                                            </div>

                                            <div className="flex min-w-full selectall_header_bg">
                                                <input
                                                    type="checkbox"
                                                    id="selectAllCheckbox"
                                                    checked={
                                                        agendaList.length > 0 &&
                                                        control._formValues.agenda?.length === agendaList.length
                                                    }
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            // ✅ Select all -> set whole agendaList
                                                            setValue("agenda", agendaList);
                                                        } else {
                                                            // ✅ Deselect all -> set empty array
                                                            setValue("agenda", []);
                                                        }
                                                    }}
                                                />
                                                <label htmlFor="selectAllCheckbox" className="font-semibold">Select All</label>
                                            </div>
                                        </div>

                                        <Controller
                                            control={control}
                                            name="agenda"
                                            defaultValue={[]}   // <-- ensures it's always an array
                                            render={({ field }) => (
                                                <div className="grid grid-cols-2 mq575:grid-cols-1 gap-2">
                                                    {agendaList?.map((agenda) => {
                                                        const isChecked = field.value?.some((a: { key: string; name: string }) => a.key === agenda.key);

                                                        return (
                                                            <div className="taskchecklist_dv" key={agenda.key}>
                                                                <label
                                                                    className={`py-2 border flex items-center rounded cursor-pointer ${isChecked ? 'border-purple-500 bg-white active_taskchecklist_dv' : 'border-gray-300'
                                                                        }`}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={() => {
                                                                            const current = field.value || []; // <-- always array
                                                                            const updated = isChecked
                                                                                ? current.filter((a: { key: string }) => a.key !== agenda.key)
                                                                                : [...current, agenda]; // add whole object
                                                                            field.onChange(updated);
                                                                        }}
                                                                    />
                                                                    {agenda.name}
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        />


                                        {/* <Controller
                                            control={control}
                                            name="agenda"
                                            render={({ field }) => (
                                                <div className="grid grid-cols-2 mq575:grid-cols-1 gap-2">
                                                    {agendaList?.map((agenda, index) => {
                                                        const isChecked = field.value?.includes(agenda?.key);
                                                        return (
                                                            <div className="taskchecklist_dv">
                                                                <label
                                                                    key={agenda?.key}
                                                                    className={`py-2 border flex items-center rounded cursor-pointer ${isChecked ? 'border-purple-500 bg-white active_taskchecklist_dv' : 'border-gray-300'
                                                                        }`}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        value={agenda?.key}
                                                                        checked={isChecked}
                                                                        onChange={() => {
                                                                            const updated = isChecked
                                                                                ? field.value.filter((t: string) => t !== agenda?.key)
                                                                                : [...field.value, agenda?.key];
                                                                            field.onChange(updated);
                                                                        }}
                                                                    // className="hidden"
                                                                    />
                                                                    {agenda?.name}
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        /> */}
                                        {errors.agenda && (
                                            <p className="text-red-600 text-sm mt-1">
                                                {typeof errors.agenda.message === 'string' ? errors.agenda.message : 'Invalid input'}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* General Meeting and members (non-client/demo meeting) */}
                            {watchMeetingType && watchMeetingID != 7 && watchMeetingType?.toLowerCase() !== 'client meeting' && !isDemoMeeting && (
                                <div className="mt-4 grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                                    <div className="grid gap-[16px]">
                                        <InputsDropdownMultipleSearchSelect
                                            label="Select Team Members*"
                                            placeHolder="Choose"
                                            options={projectMemberOptions}
                                            onSelect={handleSelectMembers}
                                            inputType="checkbox"
                                            checkedItems={selectedMembers}
                                        />

                                        <div className="reportmulselect-blg  cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                                            <p
                                                className='cursor-pointer' onClick={() => setShowMembers(true)}
                                            >
                                                View Members ({selectedMembers?.length})
                                            </p>
                                        </div>
                                    </div>

                                    {selectedMembers?.length > 0 && (
                                        <SearchableInputsDropdown
                                            label="Select Host*"
                                            initialValue={watchHostName}
                                            placeHolder="Select"
                                            options={selectedMembers}
                                            onSelect={handleSelectHost}
                                            errorMessage={errors?.hostName}
                                        />
                                    )}
                                </div>
                            )}

                            {watchMeetingType && watchMeetingID == "7" && (
                                <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                                    <SearchableInputsDropdown
                                        label="Project Name*"
                                        initialValue={watchProjectName}
                                        placeHolder="Select Project"
                                        options={projectOptions}
                                        onSelect={handleSelectProject}
                                        errorMessage={errors?.projectName}
                                    />

                                    <SearchableInputsDropdown
                                        label="Goal Name*"
                                        initialValue={watchGoalName}
                                        placeHolder="Select Goal"
                                        options={goalOptions}
                                        onSelect={handleSelectGoal}
                                        errorMessage={errors?.goalName}
                                    />

                                    {goalMembers?.length > 0 && (
                                        <div className="flex-1 flex flex-col items-start justify-start gap-2 max-w-[412px]">
                                            <InputsDropdownMultipleSearchSelect
                                                label="Select Team Members*"
                                                placeHolder="Choose"
                                                options={goalMembers}
                                                onSelect={handleSelectMembers}
                                                inputType="checkbox"
                                                checkedItems={selectedMembers}
                                            />

                                            <div className="reportmulselect-blg  cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                                                <p
                                                    className='cursor-pointer' onClick={() => setShowMembers(true)}
                                                >
                                                    View Members ({selectedMembers?.length})
                                                </p>
                                            </div>

                                          
                                        </div>
                                    )}

                                    <div>
                                          {selectedMembers?.length > 0 && (
                                                <SearchableInputsDropdown
                                                    label="Select Host*"
                                                    initialValue={watchHostName}
                                                    placeHolder="Select"
                                                    options={selectedMembers}
                                                    onSelect={handleSelectHost}
                                                    errorMessage={errors?.hostName}
                                                />
                                            )}
                                    </div>

                                </div>
                            )}

                            {/* External Attendees for non-client/demo meetings */}
                            {watchMeetingType?.toLowerCase() !== 'client meeting' && !isDemoMeeting && (
                                <div className="self-stretch flex flex-col items-start justify-start text-sm">
                                    <ExternalAttendees
                                        value={attendees}
                                        onChange={setAttendees}
                                        isSingle={false}
                                        isClientMeeting={false}
                                        agencies={extAgencies}
                                        agenciesLoading={extAgenciesLoading}
                                        onAgencySelect={fetchExtEmployees}
                                        employees={extEmployees}
                                        employeesLoading={extEmployeesLoading}
                                    />
                                </div>
                            )}

                            <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border">
                                <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                    // onClick={handleGoBack}
                                    type="button"
                                    onClick={() => navigate(-1)}
                                >
                                    <div className="relative text-sm font-body-xs-semi-bold dark:text-white text-center inline-block">
                                        Cancel
                                    </div>
                                </button>
                                <button className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                                    // disabled={(err || careDelErr) !== ''}
                                    type="submit"
                                >
                                    <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                        Save
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form >
            {showMembers && (
                <PopupForMessage
                    viewMembersOnly={true}
                    isOpen={showMembers}
                    onClose={() => setShowMembers(false)}
                    showMessage={selectedMembers.map((it: any) => (
                        <div
                            key={it}
                            className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                        >
                            <div className="flex flex-row items-center justify-start">
                                <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                                    {it.name}
                                </div>
                            </div>
                            {/* <img
                                className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                                loading="lazy"
                                alt="Remove"
                                src={closeDowpDowniconimg}
                                onClick={() => handleRemoveMember(it.key)}
                            /> */}
                        </div>
                    ))}
                />
            )
            }
            {
                (meetingTypesDataLoading || employeesDataLoading || projectsDataLoading || projectGoalsLoading || createMeetingLoading || zoomLinkLoading) && (
                    <Loader />
                )
            }
        </>
    );
};

export default AddMeeting;
